import React, {
    createContext,
    useState,
    useEffect,
    useContext,
    useMemo,
    useCallback,
} from 'react'
import { useLocation, useParams, useHistory } from 'react-router-dom'
import withConfig from './withConfig'
import toast from '../elem/Toast'
import { APIRequestContext } from './APIRequestContext'
import {
    amendConfigOptions,
    defaultReportConfig,
    generatePostRequestFromFormData,
    generateReportAttributesFromFormData,
    getReportSelectOptions,
    removeJoinRecursively,
    removeTableReferences,
} from '../../utils/reporting/config'
import * as XLSX from 'xlsx'
import dayjs from 'dayjs'
import { updateParameters } from '../../utils/reporting/queryParameters'
import { formatValueExportReport } from '../../utils/valueFormatting'
import stringFields from '../../utils/constants/widgets/stringFields'

const ReportingContext = createContext(null)

const initialState = {
    selectedFormSection: 'Report Information',
}
const ReportingContextProvider = ({ config, children }) => {
    const { API_URL } = config
    const { authenticatedFetch } = useContext(APIRequestContext)
    const [reports, setReports] = useState([])
    const [report, setReport] = useState(null)
    const location = useLocation()
    const params = useParams()
    const history = useHistory()
    const [loading, setLoading] = useState(true)
    const [isSubmitting, setSubmitting] = useState(false)
    const [loadingExport, setLoadingExport] = useState(false)
    const [reportPreview, setReportPreview] = useState(null)
    const [info, setInfo] = useState({ display: false })
    const [reportTableData, setReportTableData] = useState()
    const [selectedFormSection, setSelectedFormSection] = useState(
        initialState.selectedFormSection
    )
    const [editing, setEditing] = useState(true)
    const [reportConfig, setReportConfig] = useState(defaultReportConfig)
    const [initialReportConfig, setInitialReportConfig] =
        useState(defaultReportConfig)
    const [reportSelectOptions, setReportSelectOptions] = useState(null)
    const [reportOptions, setReportOptions] = useState(null)
    const [modal, setModal] = useState({ display: false })
    const viewOnly = useMemo(() => !editing, [editing])
    const [queryParameters, setQueryParameters] = useState({})

    const reportId = useMemo(() => {
        if (params.reportId) {
            try {
                return parseInt(params.reportId)
            } catch (e) {
                return null
            }
        } else {
            return null
        }
    }, [params])

    const updateQueryParameters = useCallback(
        (newParameters) => {
            if (reportId) {
                setQueryParameters((prevParameters) =>
                    updateParameters(newParameters, prevParameters)
                )
            }
        },
        [setQueryParameters, reportId]
    )

    // get all reports from the api on context load
    useEffect(() => {
        // fetch the reports from the api via authenticatedFetch
        const shouldLoadReports =
            location.pathname.includes('reports') ||
            location.pathname.includes('report/create') ||
            location.pathname.includes('report/edit')

        if (shouldLoadReports) {
            setLoading(true) // set the loading to true
            authenticatedFetch(`${API_URL}/report`)
                .then(async (response) => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((response) => {
                    setReports(response.data)
                })
                .catch((e) => {
                    toast({
                        level: 'error',
                        message:
                            'Reporting Landing Page:' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server'),
                    })
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }, [API_URL, location, reportId])

    const previewReport = useCallback(
        (formData) => {
            setLoading(true)
            const postData = generateReportAttributesFromFormData(
                formData,
                reportConfig,
                reportSelectOptions
            )
            authenticatedFetch(`${API_URL}/report/preview`, {
                method: 'POST',
                body: postData,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(async (response) => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((response) => {
                    setReportPreview(response)
                })
                .catch((e) => {
                    toast({
                        level: 'error',
                        message:
                            'Reporting Landing Page:' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server'),
                    })
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [
            authenticatedFetch,
            API_URL,
            setReportPreview,
            reportConfig,
            reportSelectOptions,
        ]
    )

    // get a report by id from the api via authenticatedFetch
    const initializeReport = useCallback(() => {
        // set defaults
        setReportPreview(null)
        setReportTableData(null)
        // if (!isReset) {
        setReport(null)
        setReportConfig(defaultReportConfig)
        // }
        setInfo({ display: false })
        // fetch the report from the api via authenticatedFetch
        if (reportId) {
            setLoading(true)
            authenticatedFetch(`${API_URL}/report/${reportId}`)
                .then(async (response) => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((response) => {
                    const reportData = response.data
                    // when the report options and the config have been fetched, use the config to set
                    // the visibility of certain option sets
                    let updatedFormOptions = {}
                    let updatedReportOptions = {}
                    let newConfig = defaultReportConfig
                    if (reportData && reportData.ReportAttributes) {
                        try {
                            const parsedConfig = JSON.parse(
                                reportData.ReportAttributes
                            )
                            if (
                                parsedConfig !== null &&
                                parsedConfig !== undefined &&
                                Object.keys(parsedConfig).length > 0
                            ) {
                                newConfig = amendConfigOptions(parsedConfig)
                            } else {
                                console.error(
                                    'unable to parse a valid report configuration from the report. setting to new report'
                                )
                            }
                        } catch (e) {
                            toast({
                                level: 'error',
                                message:
                                    'Reporting Landing Page: Unable to parse report config',
                            })
                            console.error(
                                'error parsing a valid report configuration from the report. setting to new report'
                            )
                        }
                    }
                    if (reportData && reportData.DatabaseName) {
                        setLoading(true)
                        authenticatedFetch(
                            `${API_URL}/report/options/${reportData.DatabaseName}`
                        )
                            .then(async (response) => {
                                if (response.ok) {
                                    return response.json()
                                } else {
                                    const error = await response.text()
                                    throw new Error(error)
                                }
                            })
                            .then((response) => {
                                updatedReportOptions = response.data
                                updatedFormOptions = getReportSelectOptions(
                                    updatedReportOptions,
                                    newConfig
                                )
                            })
                            .catch((e) => {
                                toast({
                                    level: 'error',
                                    message:
                                        'Report Options Query:' +
                                        (e.message
                                            ? e.message
                                            : 'Unable to connect to the server'),
                                })
                            })
                            .finally(() => {
                                hydrateReport(
                                    reportData,
                                    newConfig,
                                    updatedFormOptions,
                                    updatedReportOptions
                                )
                                setLoading(false)
                                return
                            })
                    } else {
                        setLoading(true)
                        authenticatedFetch(`${API_URL}/report/options`)
                            .then(async (response) => {
                                if (response.ok) {
                                    return response.json()
                                } else {
                                    const error = await response.text()
                                    throw new Error(error)
                                }
                            })
                            .then((response) => {
                                updatedReportOptions = response.data
                                updatedFormOptions = getReportSelectOptions(
                                    updatedReportOptions,
                                    newConfig
                                )
                            })
                            .catch((e) => {
                                toast({
                                    level: 'error',
                                    message:
                                        'Report Options Query:' +
                                        (e.message
                                            ? e.message
                                            : 'Unable to connect to the server'),
                                })
                            })
                            .finally(() => {
                                hydrateReport(
                                    reportData,
                                    newConfig,
                                    updatedFormOptions,
                                    updatedReportOptions
                                )
                                setLoading(false)
                            })
                    }
                })
                .catch((e) => {
                    toast({
                        level: 'error',
                        message:
                            'Reporting Landing Page:' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server'),
                    })
                })
                .finally(() => {
                    setLoading(false)
                })
        } else {
            setReport(null)
        }
    }, [reportId, API_URL, authenticatedFetch, setLoading])

    // hydrate the report with the data from the api when the report id is present
    // and we are on an edit page
    useEffect(() => {
        if (reportId && !(location.pathname === '/admin/reports')) {
            setSelectedFormSection(initialState.selectedFormSection)
            initializeReport()
        }
    }, [reportId, initializeReport, setSelectedFormSection])

    const hydrateReport = useCallback(
        (reportData, configData, selectOptionData, optionData) => {
            setReportPreview(null)
            setReport(reportData)
            setInitialReportConfig(configData)
            setReportConfig(configData)
            setReportSelectOptions(selectOptionData)
            setReportOptions(optionData)
        },
        [
            setReport,
            setReportConfig,
            setReportSelectOptions,
            setReportOptions,
            setInitialReportConfig,
        ]
    )

    // save report
    const saveReport = useCallback(
        (formData) => {
            setSubmitting(true)
            const postData = generatePostRequestFromFormData(
                formData,
                reportConfig,
                reportSelectOptions
            )

            authenticatedFetch(`${API_URL}/report/edit/${reportId}`, {
                method: 'POST',
                body: JSON.stringify(postData),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(async (response) => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((response) => {
                    toast({
                        level: 'success',
                        message: 'Report Saved',
                    })
                    initializeReport(response.data)
                    if (selectedFormSection == 'Create Report') {
                        setSelectedFormSection(initialState.selectedFormSection)
                    }
                })
                .catch((e) => {
                    toast({
                        level: 'error',
                        message:
                            'Reporting Landing Page:' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server'),
                    })
                })
                .finally(() => {
                    setSubmitting(false)
                })
        },
        [
            reportId,
            reportConfig,
            reportSelectOptions,
            authenticatedFetch,
            API_URL,
            initializeReport,
        ]
    )

    // create report
    const createReport = useCallback(
        (reportData = null) => {
            setLoading(true)

            const requestBody = reportData || {
                ReportName: 'New Report',
                ReportDescription: 'New Report',
                DatabaseName: '',
            }

            authenticatedFetch(`${API_URL}/report/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            })
                .then(async (response) => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((response) => {
                    const newReportId = response.id || response.data?.id
                    toast({
                        level: 'success',
                        message: 'New Report Created',
                    })
                    history.push(`/admin/report/edit/${newReportId}`)
                    return
                })
                .catch((e) => {
                    toast({
                        level: 'error',
                        message:
                            'Reporting Landing Page:' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server'),
                    })
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [API_URL, authenticatedFetch]
    )

    // delete report
    const deleteReport = useCallback(
        (id) => {
            authenticatedFetch(`${API_URL}/report/delete/${id}`, {
                method: 'PUT',
            })
                .then(async (response) => {
                    if (response.ok) {
                        return response.ok
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((r) => {
                    toast({
                        level: 'success',
                        message: 'Delete operation completed successfully.',
                    })
                    history.push(`/admin/reports`)
                    return
                })
                .catch((e) => {
                    toast({
                        level: 'error',
                        message:
                            'Reporting Landing Page:' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server'),
                    })
                })
        },
        [API_URL, authenticatedFetch, history]
    )

    // clone report
    const cloneReport = useCallback(
        (id) => {
            authenticatedFetch(`${API_URL}/report/clone/${id}`, {
                method: 'GET',
            })
                .then(async (response) => {
                    if (response.ok) {
                        toast({
                            level: 'success',
                            message: 'Report Cloned',
                        })
                        history.push(`/admin/reports`)
                        return
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .catch((e) => {
                    toast({
                        level: 'error',
                        message:
                            'Reporting Landing Page:' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server'),
                    })
                })
        },
        [API_URL, authenticatedFetch]
    )

    // change target database name and fetch data sources
    const changeTargetDatabase = useCallback(
        (databaseName) => {
            authenticatedFetch(`${API_URL}/report/options/${databaseName}`)
                .then(async (response) => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((response) => {
                    const updatedReportOptions = response.data

                    // update the report config with the new database name
                    // and default report config options for dependent fields
                    const updatedReportConfig = {
                        ...defaultReportConfig,
                        reportName: report.ReportName,
                        reportDescription: report.ReportDescription,
                        tags: reportConfig.tags,
                        databaseName: databaseName,
                    }
                    const updatedReportSelectOptions = getReportSelectOptions(
                        updatedReportOptions,
                        updatedReportConfig,
                        databaseName
                    )
                    setReportSelectOptions(updatedReportSelectOptions)
                    // setReportConfig(updatedReportConfig)
                    // setReport(prevReport => {
                    //     debugger
                    //     return {
                    //         ...prevReport,
                    //         DatabaseName: databaseName
                    //     }
                    // })
                })
                .catch((e) => {
                    toast({
                        level: 'error',
                        message:
                            'Reporting Landing Page:' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server'),
                    })
                })
        },
        [
            setReportSelectOptions,
            reportConfig,
            setReport,
            API_URL,
            authenticatedFetch,
        ]
    )

    const fetchReportOptions = useCallback(
        (updatedReportConfig) => {
            setLoading(true)
            authenticatedFetch(
                `${API_URL}/report/options/${updatedReportConfig.databaseName}`
            )
                .then(async (response) => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((response) => {
                    let updatedReportOptions = response.data
                    let updatedFormOptions = getReportSelectOptions(
                        updatedReportOptions,
                        updatedReportConfig
                    )
                    setReportSelectOptions((prev) => updatedFormOptions)
                })
                .catch((e) => {
                    toast({
                        level: 'error',
                        message:
                            'Report Options Query:' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server'),
                    })
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [authenticatedFetch, setLoading, setReportSelectOptions]
    )

    // delete join from report config
    const deleteJoin = useCallback(
        (joinIndex) => {
            setLoading(true)
            let updatedReportConfig = { ...reportConfig }

            const orphanedTables = removeJoinRecursively(
                joinIndex,
                updatedReportConfig
            )
            orphanedTables.forEach((joinData) => {
                removeTableReferences(joinData, updatedReportConfig)
            })
            setReportConfig(updatedReportConfig)

            return updatedReportConfig
        },
        [reportConfig, setReportConfig, reportSelectOptions]
    )

    // get the report data from the api
    const runReport = useCallback(() => {
        setLoading(true)
        authenticatedFetch(
            `${API_URL}/report/run/${reportId}?page=${1}&pageSize=${25}`
        )
            .then(async (response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    const error = await response.text()
                    throw new Error(error)
                }
            })
            .then((response) => {
                setReportTableData(response.data)
            })
            .catch((e) => {
                toast({
                    level: 'error',
                    message:
                        'Reporting Landing Page:' +
                        (e.message
                            ? e.message
                            : 'Unable to connect to the server'),
                })
            })
            .finally(() => {
                setLoading(false)
            })
    }, [
        reportId,
        setReportTableData,
        setLoading,
        // queryParameters,
    ])

    const exportReport = useCallback(async () => {
        try {
            setLoadingExport(true)
            //avoid blocking the main thread while processing        
            await new Promise(resolve => setTimeout(resolve, 0));
    
            let gridData = queryParameters?.currentReportData || []
            const reportName = report.ReportName

            let filteredExportData = []
            gridData.forEach((i) => {
                const filteredObj = Object.keys(i).reduce((obj, key) => {
                    obj[key] = stringFields.includes(key)
                        ? i[key]
                        : formatValueExportReport(i[key])
                    return obj
                }, {})
                filteredExportData.push(filteredObj)
            })

            const dateString = dayjs(Date.now()).format('YYYY-MM-DD')
            let filteredGridFieldsExist =
                queryParameters?.filters?.length &&
                queryParameters.currentReportData
            const downloadName = `${reportName}${
                filteredGridFieldsExist ? '_Filtered' : ''
            }_${dateString}.xlsx`

            const wb = XLSX.utils.book_new()
            const ws = XLSX.utils.json_to_sheet(filteredExportData)
            XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
            XLSX.writeFile(wb, downloadName)
        } catch (error) {
            console.error('Export failed:', error)
        } finally {
            setLoadingExport(false)
        }
    }, [report, queryParameters, stringFields, setLoadingExport])

    const resetForm = useCallback(() => {
        initializeReport()
    }, [initializeReport])

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [clickedTab, setClickedTab] = useState(null)
    const [reroute, setReroute] = useState(null)

    const navigateAndDiscard = useCallback(() => {
        resetForm() //if user clicks Discard Changes, restart the form and continue
        if (!!reroute) {
            history.push(reroute)
            setReroute(null)
        } else {
            setSelectedFormSection(clickedTab)
            setClickedTab(null)
        }
        setHasUnsavedChanges(false)
    }, [
        setSelectedFormSection,
        resetForm,
        clickedTab,
        reroute,
        setReroute,
        history,
    ])

    return (
        <ReportingContext.Provider
            value={{
                loading,
                reports,
                reportId,
                report,
                info,
                setInfo,
                reportConfig,
                previewReport,
                reportPreview,
                selectedFormSection,
                setSelectedFormSection,
                reportSelectOptions,
                saveReport,
                createReport,
                deleteReport,
                cloneReport,
                changeTargetDatabase,
                deleteJoin,
                editing,
                setEditing,
                viewOnly,
                runReport,
                reportTableData,
                exportReport,
                modal,
                setModal,
                initialReportConfig,
                resetForm,
                updateQueryParameters,
                queryParameters,
                loadingExport,
                isSubmitting,
                setSubmitting,
                hasUnsavedChanges,
                setHasUnsavedChanges,
                navigateAndDiscard,
                clickedTab,
                setClickedTab,
                setReroute,
            }}
        >
            {children}
        </ReportingContext.Provider>
    )
}

export { ReportingContext }
export default withConfig(ReportingContextProvider)
